import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from '@/lib/auth-context';
import { WorkspaceProvider } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';
import AppShell from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Auth pages
import AuthLoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import SetPasswordPage from '@/pages/auth/SetPasswordPage';

// V2 pages (stubs — will be filled in 1.x tasks)
import DashboardPage from '@/pages/DashboardPage';

// Legacy pages (still accessible by direct URL)
import VmsPage from '@/pages/VmsPage';
import VmDetailPage from '@/pages/VmDetailPage';
import JobsPage from '@/pages/JobsPage';
import JobDetailPage from '@/pages/JobDetailPage';
import BatchesPage from '@/pages/BatchesPage';
import BatchDetailPage from '@/pages/BatchDetailPage';
import ContactsPage from '@/pages/ContactsPage';
import ContactDetailPage from '@/pages/ContactDetailPage';
import OutputsPage from '@/pages/OutputsPage';
import FormsPage from '@/pages/FormsPage';
import AuditPage from '@/pages/AuditPage';
import LogsPage from '@/pages/LogsPage';
import WebhooksPage from '@/pages/WebhooksPage';
import SettingsLayout from '@/pages/settings/SettingsLayout';
import KeysPage from '@/pages/settings/KeysPage';
import VmsSettingsPage from '@/pages/settings/VmsSettingsPage';
import SecretsPage from '@/pages/settings/SecretsPage';
import AccountPage from '@/pages/settings/AccountPage';
import DomainsPage from '@/pages/settings/domains/DomainsPage';
import EmailAccountsPage from '@/pages/settings/email-accounts/EmailAccountsPage';
import GroupsPage from '@/pages/settings/email-accounts/GroupsPage';
import LabelMappingsPage from '@/pages/settings/label-mappings/LabelMappingsPage';

// V2 stub pages
import CampaignsListPage from '@/pages/campaigns/CampaignsListPage';
import CampaignDetailPage from '@/pages/campaigns/CampaignDetailPage';
import NewCampaignWizard from '@/pages/campaigns/new/NewCampaignWizard';
import LeadsPage from '@/pages/leads/LeadsPage';
import InboxPage from '@/pages/inbox/InboxPage';
import OutreachPage from '@/pages/outreach/OutreachPage';
import OutreachAgentsPage from '@/pages/outreach/OutreachAgentsPage';
import OutreachAgentDetailPage from '@/pages/outreach/OutreachAgentDetailPage';
import OutreachLeadsPage from '@/pages/outreach/OutreachLeadsPage';
import EnrichersStubPage from '@/pages/stubs/EnrichersPage';
import MonitoringStubPage from '@/pages/stubs/MonitoringPage';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              className: 'font-body',
              style: { borderRadius: '15px', fontSize: '13px' },
            }}
          />
          <Routes>
            {/* Public routes */}
            <Route path={ROUTES.LOGIN} element={<AuthLoginPage />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={ROUTES.SET_PASSWORD} element={<SetPasswordPage />} />

            {/* Root redirect */}
            <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              {/* V2 routes */}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="campaigns" element={<CampaignsListPage />} />
              <Route path="campaigns/new" element={<NewCampaignWizard />} />
              <Route path="campaigns/:id" element={<CampaignDetailPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="outreach" element={<OutreachPage />} />
              <Route path="outreach/agents" element={<OutreachAgentsPage />} />
              <Route path="outreach/agents/:id" element={<OutreachAgentDetailPage />} />
              <Route path="outreach/leads" element={<OutreachLeadsPage />} />
              <Route path="enrichers" element={<EnrichersStubPage />} />
              <Route path="monitoring" element={<MonitoringStubPage />} />

              {/* Legacy routes */}
              <Route path="vms" element={<VmsPage />} />
              <Route path="vms/:id" element={<VmDetailPage />} />
              <Route path="jobs" element={<JobsPage />} />
              <Route path="jobs/:id" element={<JobDetailPage />} />
              <Route path="batches" element={<BatchesPage />} />
              <Route path="batches/:id" element={<BatchDetailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/:id" element={<ContactDetailPage />} />
              <Route path="outputs" element={<OutputsPage />} />
              <Route path="forms" element={<FormsPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
              <Route
                path="settings"
                element={
                  <ProtectedRoute roles={['admin', 'operator']}>
                    <SettingsLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="keys" replace />} />
                <Route path="keys" element={<KeysPage />} />
                <Route path="vms" element={<VmsSettingsPage />} />
                <Route path="secrets" element={<SecretsPage />} />
                <Route path="domains" element={<DomainsPage />} />
                <Route path="email-accounts" element={<EmailAccountsPage />} />
                <Route path="email-accounts/groups" element={<GroupsPage />} />
                <Route path="label-mappings" element={<LabelMappingsPage />} />
                <Route path="account" element={<AccountPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          </Routes>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
